import { ComponentType } from 'react';

import { Brand } from '../types';
import {
  WHLogoUSA,
  WHLogoUsaSmall,
  WHSportsbookLogo,
  CaesarsLogo,
  CaesarsLogoSmall,
  CaesarsRaceLogo,
  CaesarsSportsbookLogo,
  DESportspickLogo,
  DEBallysLogo,
  DEHarringtonLogo,
  DEDelawareParkLogo,
  NoEventsCzr,
  NoEventsWH,
  NoSelectionCzr,
  NoSelectionWH,
  GlobalErrorCzr,
  GlobalErrorWH,
  ScanTicketCzr,
  ScanTicketWH
} from '../assets/images';
import { StateCode } from '../types';
import { BetAhead } from '../assets/icons';
import { useDeviceInfo } from '../retail-config/device';
import { BrandLogoBackground } from '../themes';

export type BrandData = {
  homePageImage: string;
  homePageImageHeight: string;
  brandLogo: ComponentType<{
    height?: string;
    onBackground: BrandLogoBackground;
    width?: string;
  }> | null;
  brandLogoSmall: ComponentType<any> | null;
  raceLogo: ComponentType<any> | null;
  dokBrandLogo: ComponentType<any> | null;
  brandName: Brand;
  BetAheadButton: ComponentType<any>;
  NoEventsImage: ComponentType<any>;
  NoSelectionsImage: ComponentType<any> | null;
  GlobalErrorImage: ComponentType<any> | null;
  ScanTicketImage: ComponentType<any> | null;
  mobileDeposit?: string;
  betBuilder?: string;
  prettyName: string;
  supportIA: string;
  supportIN: string;
  supportMD: string;
  supportWV: string;
  customerServiceNumber: string;
};

const betBuilderLogoWilliamHill = '/images/william-hill/betbuilder.webp';

const defaultValues: BrandData = {
  brandName: Brand.GENERIC,
  homePageImage: '/images/homepage_generic.webp',
  homePageImageHeight: '100%',
  brandLogo: null,
  brandLogoSmall: null,
  raceLogo: null,
  dokBrandLogo: null,
  supportIA: 'Support-IA@williamhill.us',
  supportIN: 'Support-IN@williamhill.us',
  supportMD: 'support-md@caesarssportsbook.com',
  supportWV: 'support-WV@williamhill.us',
  prettyName: 'William Hill',
  NoEventsImage: NoEventsWH,
  NoSelectionsImage: NoSelectionWH,
  GlobalErrorImage: GlobalErrorWH,
  ScanTicketImage: ScanTicketWH,
  BetAheadButton: BetAhead,
  customerServiceNumber: '(855) 754-1200'
};

const getBrandValues = (brand: Brand): BrandData => {
  switch (brand) {
    case Brand.WilliamHill:
      return {
        ...defaultValues,
        homePageImage: '/images/homepage_resting_stamp.webp',
        homePageImageHeight: '50%',
        brandLogo: WHLogoUSA,
        brandLogoSmall: WHLogoUsaSmall,
        raceLogo: WHLogoUsaSmall,
        dokBrandLogo: WHSportsbookLogo,
        brandName: Brand.WilliamHill,
        NoEventsImage: NoEventsWH,
        NoSelectionsImage: NoSelectionWH,
        GlobalErrorImage: GlobalErrorWH,
        ScanTicketImage: ScanTicketWH,
        mobileDeposit: '/images/william-hill/mobile-deposit.webp',
        betBuilder: betBuilderLogoWilliamHill
      };
    case Brand.Caesars:
      return {
        homePageImage: '/images/homepage_caesars_sb.svg',
        homePageImageHeight: '70%',
        brandLogo: CaesarsLogo,
        brandLogoSmall: CaesarsLogoSmall,
        raceLogo: CaesarsRaceLogo,
        dokBrandLogo: CaesarsSportsbookLogo,
        brandName: Brand.Caesars,
        BetAheadButton: BetAhead,
        NoEventsImage: NoEventsCzr,
        NoSelectionsImage: NoSelectionCzr,
        GlobalErrorImage: GlobalErrorCzr,
        ScanTicketImage: ScanTicketCzr,
        prettyName: 'Caesars',
        supportIA: 'support-ia@caesarssportsbook.com',
        supportIN: 'support-in@caesarssportsbook.com',
        supportWV: 'support-wv@caesarssportsbook.com',
        supportMD: 'support-md@caesarssportsbook.com',
        customerServiceNumber: '(855) 474-0606',
        mobileDeposit: '/images/caesars/mobile-deposit.webp',
        betBuilder: '/images/czr/betbuilder.webp'
      };
    default:
      return defaultValues;
  }
};

export default function useBrand(): BrandData {
  const deviceInfo = useDeviceInfo();

  if (deviceInfo?.slotId.state === StateCode.Arizona) {
    return {
      ...getBrandValues(deviceInfo.brand),
      homePageImage: '/images/homepage_caesars_sb_az.webp'
    };
  }

  if (deviceInfo?.slotId.state === StateCode.Maryland) {
    return {
      ...getBrandValues(deviceInfo.brand),
      homePageImage: '/images/homepage_caesars_sb_md.svg'
    };
  }

  if (deviceInfo?.slotId.state === StateCode.Delaware) {
    const Logo = getDelawareLogoOverride(deviceInfo.slotId.venueId);
    return {
      ...getBrandValues(deviceInfo.brand),
      brandLogoSmall: Logo,
      raceLogo: Logo,
      betBuilder: betBuilderLogoWilliamHill
    };
  }

  if (deviceInfo?.brand) {
    return getBrandValues(deviceInfo.brand);
  }

  return getBrandValues(Brand.GENERIC);
}

function getDelawareLogoOverride(venueId: string) {
  const logoMap = {
    DE00001: DEBallysLogo,
    DE00002: DEDelawareParkLogo,
    DE00003: DEHarringtonLogo
  };
  return logoMap[venueId] || DESportspickLogo;
}
